/**
 * leadRealToFocusShape.js
 * ───────────────────────
 * Adapter: maps a *real-backend* lead document (the shape returned by
 * `GET /api/leads/{id}` or the slim list projection) into the rich shape
 * that `LeadFocus.jsx` + `SalesDialerLive.jsx` were designed for
 * (which originally only handled the curated `DEMO_LEADS` fixtures).
 *
 * Demo-only fields (medicare benes, NPPES specialty groups, top HCPCS
 * codes, top drug, demographics, EHR vendor blocks, etc.) are emitted as
 * `null` / empty arrays — the consumer pages already render "—" for
 * those when missing, so the real-mode UI degrades gracefully without
 * surfacing fake numbers.
 *
 * Two public exports:
 *   - `isRealLeadId(id)`  — heuristic: real-backend IDs are 36-char
 *                            UUIDs; demo IDs are `lead-<NPI>` or
 *                            `lead-<n>` strings.
 *   - `realLeadToFocusShape(lead)` — produces the focus-shape object.
 *
 * If you need to render a real lead inside `LeadFocus` or
 * `SalesDialerLive`, push it through this function first.
 */

/** True if `id` looks like a real backend UUID rather than a demo lead-* slug. */
export function isRealLeadId(id) {
  if (!id || typeof id !== "string") return false;
  if (id.startsWith("lead-")) return false; // demo fixtures
  // RFC 4122 / loose UUID-ish: 36 chars with 4 dashes.
  return id.length >= 32 && (id.includes("-") || /^[a-f0-9]{32,}$/i.test(id));
}

/**
 * Map a real backend lead into the LeadFocus-compatible shape.
 * Unmapped (demo-only) fields are returned as null / [] so consumers
 * fall back to their empty-state rendering.
 */
export function realLeadToFocusShape(lead) {
  if (!lead || typeof lead !== "object") return null;
  const enr = lead.enrichment || {};

  // Primary contact — synthesise from the flat backend fields.
  const primaryContact = {
    id: lead.contact_id || `${lead.id}-c1`,
    name: lead.contact_name || lead.title || "Unknown contact",
    role: lead.title && lead.contact_name && lead.title !== lead.contact_name ? lead.title : (lead.role || ""),
    phone: lead.phone || "",
    email: lead.email || "",
    primary: true,
  };
  const contacts = Array.isArray(lead.contacts) && lead.contacts.length > 0
    ? lead.contacts
    : [primaryContact];

  // Locations — pull from the flat address fields if present.
  // NOTE: the Vitals card reads `loc.address`, `loc.city`, `loc.state`, `loc.zip`,
  // `loc.phone`, `loc.label`. Earlier versions stored the street under `line1`
  // which left the address row blank for Ampliz-imported leads — we now emit
  // both keys so the renderer + any line1-based callers both light up.
  const primaryLocation = (lead.city || lead.state || lead.address || lead.zip) ? [{
    id: `${lead.id}-l1`,
    label: "Practice address",
    address: lead.address || lead.street || "",
    line1:   lead.address || lead.street || "",
    city:  lead.city  || "",
    state: lead.state || "",
    zip:   lead.zip   || lead.zip_code || "",
    phone: lead.phone || "",
    primary: true,
  }] : [];
  const locations = Array.isArray(lead.locations) && lead.locations.length > 0
    ? lead.locations
    : primaryLocation;

  return {
    // Iter 139.2 (2026-05-26 stephanie@ehiehr.com — issue reports
    //   #a118827e, #66644b9b, #531e7d2e): spread the original lead doc
    //   FIRST so every field the backend returns survives the reshape.
    //   Previously this hand-built return object silently DROPPED any
    //   CSV-template field (practice_type, providers_multi,
    //   assigned_users, followers, lead_status, lead_tag, time_zone,
    //   organizational_role, specialty_multi, key_decision_makers, …
    //   ~60 fields), so reps saw their saves succeed → vanish on the
    //   very next mount because the page re-shaped them away. The
    //   curated overrides below still take precedence for the keys
    //   they touch (stage, contacts/locations adapter shape, ai_score
    //   normalisation, enrichment projections, etc.).
    ...lead,
    // Identifiers / labels
    id: lead.id,
    clinic: lead.company || lead.title || lead.contact_name || "Lead",
    company: lead.company || "",
    npi: lead.npi || null,
    web: lead.website || lead.web || "",
    website: lead.website || lead.web || "",

    // Flat practice-level contact fields — passed through so the CMS
    // Profile tab + Lead header can render them without digging through
    // the synthesised contacts/locations arrays. Ampliz lands these on
    // the lead doc directly (`lead.address`, `lead.phone`, `lead.email`).
    address: lead.address || lead.street || "",
    city:    lead.city  || "",
    state:   lead.state || "",
    zip:     lead.zip   || lead.zip_code || "",
    phone:   lead.phone || "",
    email:   lead.email || "",

    // Contact / location
    contacts,
    locations,

    // Pipeline state
    stage: lead.stage || lead.lead_stage || "new_lead",
    sub_status: lead.sub_status || "",
    lead_stage: lead.lead_stage || lead.stage || "",
    lead_source: lead.lead_source || lead.source || "",
    lead_rating: lead.lead_rating || "",
    value: lead.value || 0,
    probability: lead.probability || null,
    budget_range: lead.budget_range || "",
    next_action: lead.next_action || "",
    last_contact_date: lead.last_contact_date || lead.updated_at || null,
    days_stale: lead.days_stale || 0,
    owner_id: lead.owner_id || null,
    // Iter 181 (Feb 2026) — surface contact-attempts count so the Sales
    // Dialer's classifyLead() can bucket never-touched leads separately
    // from re-added follow-ups. Accept either the array (full /leads/{id}
    // response) or the pre-computed count (from _queue_lead_snapshot).
    contact_attempts_count: (typeof lead.contact_attempts_count === "number")
      ? lead.contact_attempts_count
      : (Array.isArray(lead.contact_attempts) ? lead.contact_attempts.length : 0),

    // AI scoring — defensive against engine dict shape
    ai_score: (typeof lead.ai_score === "number") ? lead.ai_score : (lead.ai_score?.primary?.score ? Math.round(lead.ai_score.primary.score) : 0),

    // Specialty (best-effort map — real backend stores it on `specialty`,
    // but if enrichment ran we prefer the verified NPPES taxonomy)
    specialty: enr.nppes?.primary_specialty || lead.specialty || "",
    credential: enr.nppes?.credential || lead.credential || "",
    nppes: {
      specialty: enr.nppes?.primary_specialty || lead.specialty || "",
      specialty_group: enr.nppes?.primary_specialty || lead.specialty || lead.industry || "",
      credential: enr.nppes?.credential || lead.credential || "",
      specialty_type: lead.specialty_type || "",
      taxonomy_code: enr.nppes?.primary_specialty_code || "",
      enumeration_type: enr.nppes?.enumeration_type || "",
      practice_address: enr.nppes?.practice_address || null,
    },

    // Enrichment projections → legacy demo-shape so all existing cards
    // "just work" once /api/leads/{id}/enrich has populated `lead.enrichment.*`.
    practice: { group_size: lead.group_size || null, web: lead.website || "" },
    // ── CMS / Ampliz fields ──────────────────────────────────────
    // Source precedence: Ampliz vendor import (`lead.medicare.*`) > CMS PUF
    // enrichment (`enr.cms.*`). Ampliz lands these straight on the lead
    // doc with the same nested shape, so we pass them through verbatim
    // and only fall back to the enrichment-derived projection when the
    // lead carries no Ampliz block. Same idea for `ehr`, `demographics`,
    // `drugs`, `top_hcpcs`, etc. Without this pass-through the new
    // CMS Profile tab (CmsProfileTab.jsx) renders empty even though the
    // backend has the data.
    medicare: lead.medicare || (enr.cms?.found ? {
      beneficiaries: enr.cms.beneficiaries ?? null,
      services: enr.cms.total_services ?? null,
      allowed: enr.cms.allowed_amount ?? null,
      submitted: enr.cms.submitted_charges ?? null,
      paid: enr.cms.medicare_payment ?? null,
      avg_per_bene: enr.cms.avg_allowed_per_beneficiary ?? null,
      data_year: enr.cms.data_year ?? null,
      provider_state: enr.cms.provider_state ?? null,
      provider_specialty: enr.cms.provider_specialty ?? null,
    } : null),
    ehr: (lead.ehr && lead.ehr.vendor) ? lead.ehr : (enr.chpl?.found && (enr.chpl.products || []).length > 0 ? {
      vendor: enr.chpl.products[0].developer || enr.chpl.vendor || "",
      product: enr.chpl.products[0].product_name || "",
      version: enr.chpl.products[0].version || "",
      edition: enr.chpl.products[0].edition || "",
      certification_date: enr.chpl.products[0].certification_date || "",
      chpl_id: enr.chpl.products[0].chpl_id || "",
      products: enr.chpl.products,
    } : null),
    signals:      lead.signals      || {},
    drugs:        lead.drugs        || null,
    suppliers:    lead.suppliers    || null,
    demographics: lead.demographics || null,
    products:     Array.isArray(lead.products) ? lead.products : null,
    top_drug:     lead.drugs?.top_drug || null,
    top_hcpcs:    Array.isArray(lead.top_hcpcs) ? lead.top_hcpcs
                  : (Array.isArray(enr.hcpcs?.top) ? enr.hcpcs.top : []),
    // Practice-level identifiers + signals that the Ampliz importer lands
    // directly on the lead doc. The CMS Profile tab reads these straight
    // off `lead.*`, so we preserve them through the adapter.
    aco_participation: lead.aco_participation ?? null,
    billing_status:    lead.billing_status || "",
    insurance_type:    lead.insurance_type || "",
    tin:               lead.tin    || "",
    pac_id:            lead.pac_id || "",
    external_ids:      lead.external_ids || {},
    enrichment:        enr,
    enriched_at:       enr.enriched_at || null,
    // NPPES drift suggestions surfaced by the reconciler — drives the
    // "Apply NPPES updates" banner above the Lead Focus grid.
    nppes_suggestions: Array.isArray(lead.nppes_suggestions) ? lead.nppes_suggestions : [],
    field_provenance:  lead.field_provenance || {},

    // Products / qualification — backend may return these as either arrays
    // OR delimited strings depending on field source. Normalise to a
    // consistent delimited-string here so consumers don't have to keep
    // re-implementing Array.isArray guards. Downstream callers can split on
    // /[;,]/ if they need a list.
    products_of_interest: Array.isArray(lead.product_interest)
      ? lead.product_interest.join("; ")
      : Array.isArray(lead.products_of_interest)
        ? lead.products_of_interest.join("; ")
        : (lead.product_interest || lead.products_of_interest || ""),
    pain_point_category: lead.pain_point_category || "",
    pain_points: Array.isArray(lead.pain_points) ? lead.pain_points.join("; ") : (lead.pain_points || ""),
    prospect_decision_timeline: lead.prospect_decision_timeline || "",

    // Activity / notes (empty in real mode until the comms pipeline lands)
    // Iter 139.2 (stephanie@ehiehr.com #a118827e): the activities the
    //   user adds during a session land in `lead.activities` (plural,
    //   $push'd by POST /leads/{id}/activities). The legacy `activity`
    //   key the timeline UI reads was sourced ONLY from `lead.activity_log`
    //   — which is empty for most leads — so on refresh the 14 entries
    //   the rep just logged disappeared. Merge both sources, prefer
    //   `activities` (the source of truth), and normalise the entry
    //   shape so the ActivityTab renderer works either way.
    activity: (() => {
      const fromActivities = Array.isArray(lead.activities) ? lead.activities : [];
      const fromActivityLog = Array.isArray(lead.activity_log) ? lead.activity_log : [];
      const all = [...fromActivities, ...fromActivityLog].map((e) => ({
        ...e,
        kind: e.kind || e.type || "system",
        text: e.text || e.title || e.summary || "",
        // Iter 170.8 (Lisa #c8185fda 2026-06-05) — the activity timeline
        // was always rendering the raw UUID stored as `by` because it
        // short-circuited before checking `actor_name`. Prefer the
        // human-readable name first (backend hydrates `actor_name`
        // via a users-map lookup on GET /leads/{id}).
        by:   e.actor_name || e.actor_email || e.by_name || e.by || e.actor_id || "—",
        at:   e.at   || e.timestamp || e.created_at || null,
      })).filter((e) => e.at);
      // Newest first
      all.sort((a, b) => new Date(b.at) - new Date(a.at));
      return all;
    })(),
    // Per-lead timeline activities (email_sent/email_received pushed by the
    // M365 poller, and email_sent rows from /m365/email/send). Keeps the
    // shape source-of-truth so EmailTab/CallLogTab can render real M365 emails.
    activities: Array.isArray(lead.activities) ? lead.activities : [],
    notes: Array.isArray(lead.notes) ? lead.notes : [],
    files: Array.isArray(lead.files) ? lead.files : [],
    meetings: Array.isArray(lead.meetings) ? lead.meetings : [],
    tasks: Array.isArray(lead.tasks) ? lead.tasks : [],
    emails: Array.isArray(lead.emails) ? lead.emails : [],
    sms: Array.isArray(lead.sms) ? lead.sms : [],
    calls: Array.isArray(lead.calls) ? lead.calls : [],
    attachments: Array.isArray(lead.attachments) ? lead.attachments : [],
    stageDates: Array.isArray(lead.stageDates) ? lead.stageDates : [],
    documents: Array.isArray(lead.documents) ? lead.documents : [],

    // Timestamps
    created_at: lead.created_at || null,
    updated_at: lead.updated_at || null,
    created_by: lead.created_by || "",

    // Marker for downstream code that wants to know if this is a real-shape lead.
    _real: true,
  };
}
